import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import { Stack, Typography, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import WalletSelector from '../../../features/Transactions/WalletSelector';
import IconCircleWrapper from '../../../ui/IconCircleWrapper';
import { CaretRightIcon } from '../../../ui/icons';
import DatePickerComponent from './DatePickerComponent';
import DescriptionField from './DescriptionField';

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface RepayTransactionFormProps {
  amount: number | null;
  setLoanWallet: (wallet: Wallet | null) => void;
  setRepayWallet: (wallet: Wallet | null) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  setDescription: (description: string) => void;
  // Initial values for editing
  initialLoanWallet?: Wallet | null;
  initialRepayWallet?: Wallet | null;
  initialDatePicker?: Dayjs | undefined;
  initialDescription?: string;
}

type Step = {
  condition: () => boolean;
  action: () => void;
};

const RepayTransactionForm = (props: RepayTransactionFormProps) => {
  const theme = useTheme();
  const [firstRender, setFirstRender] = useState(true);
  const { t } = useTranslation(['common', 'transactions', 'loan']);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const { transactionId } = useParams<{ transactionId: string }>();
  const isUpdate = !!transactionId;

  // Destructure props for cleaner code and proper useEffect dependencies
  const {
    amount,
    setLoanWallet: propsSetLoanWallet,
    setRepayWallet: propsSetRepayWallet,
    setDatePicker: propsSetDatePicker,
    setDescription: propsSetDescription,
    initialLoanWallet,
    initialRepayWallet,
    initialDatePicker,
    initialDescription,
  } = props;

  const [loanWallet, setLoanWallet] = useState<Wallet | null>(initialLoanWallet || null);
  const [repayWallet, setRepayWallet] = useState<Wallet | null>(initialRepayWallet || null);
  const [datePicker, setDatePicker] = useState<Dayjs | undefined>(initialDatePicker || dayjs());
  const [description, setDescription] = useState<string>(initialDescription || '');
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [openDialogSelectLoanWallets, setOpenDialogSelectLoanWallets] =
    useState<boolean>(false);
  const [openDialogSelectRepayWallets, setOpenDialogSelectRepayWallets] =
    useState<boolean>(false);

  const handleOnCloseDialog = () => {
    setOpenDialogSelectLoanWallets(false);
    setOpenDialogSelectRepayWallets(false);
  };

  const stepsConfig: Step[] = [
    {
      condition: () => !!amount,
      action: () => null,
    },
    {
      condition: () => !!loanWallet,
      action: () => setOpenDialogSelectLoanWallets(true),
    },
    {
      condition: () => !!repayWallet,
      action: () => setOpenDialogSelectRepayWallets(true),
    },
    {
      condition: () => !!datePicker,
      action: () => setOpenDatePicker(true),
    },
    {
      condition: () => !!description,
      action: () => {
        descriptionInputRef.current?.focus();
        descriptionInputRef.current?.click();
      },
    }
  ];

  // Update internal state when initial values change
  useEffect(() => {
    if (initialLoanWallet !== undefined) {
      setLoanWallet(initialLoanWallet);
      propsSetLoanWallet(initialLoanWallet);
    }
  }, [initialLoanWallet, propsSetLoanWallet]);

  useEffect(() => {
    if (initialRepayWallet !== undefined) {
      setRepayWallet(initialRepayWallet);
      propsSetRepayWallet(initialRepayWallet);
    }
  }, [initialRepayWallet, propsSetRepayWallet]);

  useEffect(() => {
    if (initialDatePicker !== undefined) {
      setDatePicker(initialDatePicker);
      propsSetDatePicker(initialDatePicker);
    }
  }, [initialDatePicker, propsSetDatePicker]);

  useEffect(() => {
    if (initialDescription !== undefined) {
      setDescription(initialDescription);
      propsSetDescription(initialDescription);
    }
  }, [initialDescription, propsSetDescription]);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
    }

    if (isUpdate) return;

    for (const step of stepsConfig) {
      if (!step.condition()) {
        step.action();
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, loanWallet, repayWallet, datePicker, isUpdate, firstRender]);

  return (
    <>
      <Stack spacing={4} width="100%">
        {/* Loan Wallet */}
        <Stack
          direction="row"
          spacing={theme.spacing(2)}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <PersonIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          </Stack>
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectLoanWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('transaction_form.repayment_destination_wallet', {
                ns: 'loan',
              })}
            </Typography>
            {loanWallet?.wallet ? (
              <Typography variant="h3">
                {loanWallet.wallet.name}
              </Typography>
            ) : (
              <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
            )}
          </Stack>
        </Stack>

        {/* Repay Wallet */}
        <Stack
          direction="row"
          spacing={theme.spacing(2)}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          </Stack>
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectRepayWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('transaction_form.repayment_source_wallet', { ns: 'loan' })}
            </Typography>
            {repayWallet?.wallet ? (
              <Typography variant="h3">
                {repayWallet.wallet.name}
              </Typography>
            ) : (
              <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
            )}
          </Stack>
        </Stack>

        {/* Date */}
        <DatePickerComponent
          datePicker={datePicker}
          setDatePicker={(date: Dayjs | undefined) => {
            setDatePicker(date);
            props?.setDatePicker(date);
          }}
          openDatePicker={openDatePicker}
          setOpenDatePicker={setOpenDatePicker}
          clearable={false}
        />

        {/* Description */}
        <DescriptionField
          description={description}
          setDescription={(value: string) => {
            setDescription(value);
            props?.setDescription(value);
          }}
          ref={descriptionInputRef}
        />
      </Stack>

      <WalletSelector
        open={openDialogSelectLoanWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setLoanWallet(data);
          props?.setLoanWallet(data);
          setOpenDialogSelectLoanWallets(false);
        }}
        walletTypes={['LOAN']}
        customTitle={t('transaction_form.repayment_destination_wallet_title', { ns: 'loan', defaultValue: 'Bạn trả nợ ai?' })}
      />
      <WalletSelector
        open={openDialogSelectRepayWallets}
        onClose={handleOnCloseDialog}
        onSelectWallet={(data: any) => {
          setRepayWallet(data);
          props?.setRepayWallet(data);
          setOpenDialogSelectRepayWallets(false);
        }}
        walletTypes={['BASIC', 'CREDIT']}
        customTitle={t('transaction_form.repayment_source_wallet_title', { ns: 'loan', defaultValue: 'Chọn ví chuyển tiền' })}
      />
    </>
  );
};

export default RepayTransactionForm;
